import React, { useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import assetsManager from "../assets/assetsmanager/assestsmanager";
import Pagination from "../component/Pagination";
import UploadUrlModal from "../component/UploadUrlModal";
import AlertModal from "../component/AlertModal";
import { uploadUrl } from "../hooks/uploadUrl";
import { deleteUrl } from "../hooks/deleteUrl";
import { AppContext } from "../context/context";

import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const Url = () => {
  //static messages for modals
  const rescrapeAlertMessage = "Are you sure you want to re-scrape the URL?";
  const deleteAlertMessage = "Are you sure you want to delete?";

  //global states
  const { state, fetchDocuments } = useContext(AppContext);
  const { urlData } = state;

  //modal states
  const [delelteUrlModalOpen, setDelelteUrlModalOpen] = useState(false);
  const [uploadUrlModalOpen, setUploadUrlModalOpen] = useState(false);
  const [editUrlModalOpen, setEditUrlModalOpen] = useState(false);

  //pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);

  //table loading
  const [loading, setLoading] = useState(true);

  //table data
  const [urlDataLocal, setUrlDataLocal] = useState(urlData);

  //data states
  const [searchInput, setSearchInput] = useState("");
  const [selectedUrl, setSelectedUrl] = useState(null);
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);

  //handle pagination
  const handleChangePage = (page) => {
    setPage(page);
  };

  //handle search input
  const handleChangeSearchInput = (e) => {
    setSearchInput(e.target.value);
    handleSearchUrl(e.target.value);
    setPage(0);
  };

  //handle search functionality
  const handleSearchUrl = (input) => {
    let filteredData = urlData?.filter((item) => {
      return (
        item?.name?.toLowerCase()?.includes(input?.toLowerCase()) ||
        item?.url?.toLowerCase()?.includes(input?.toLowerCase())
      );
    });
    setUrlDataLocal(filteredData);
  };

  //handle upload modal open
  const handleUpload = () => {
    setUploadUrlModalOpen(true);
  };

  //handle edit url
  const handleEditUrl = (item) => {
    setEditUrlModalOpen(true);
    setSelectedUrl(item.url);
  };

  //handle delete url
  const handleDeleteUrl = (item) => {
    setDelelteUrlModalOpen(true);
    setSelectedUrl(item.url);
  };

  //handle open url
  const handleOpenUrl = (url) => {
    window.open(url, "_blank");
  };

  //fetch url data
  const getData = async () => {
    setLoading(true);
    await fetchDocuments(state?.activeTab);
    setLoading(false);
  };

  //handle edit/rescrape url
  const handleEdit = async () => {
    toast.dismiss();
    let toastId = toast.loading("loading...");
    setLoading(true);
    try {
      let data = await uploadUrl(selectedUrl, state?.activeTab);
      toast.dismiss(toastId);
      if (data.status === "Docs uploaded successfully.") {
        toast.success(`URL(${selectedUrl}) rescraped successfully.`);
      } else {
        toast.error(`URL (${selectedUrl}) ${data.status ? data.status : ""}`);
      }
    } catch (err) {
      console.log("Error: ", err);
    }
    setLoading(false);
    setEditUrlModalOpen(false);
    fetchDocuments(state?.activeTab);
  };

  //handle delete url in modal
  const handleDelete = async () => {
    toast.dismiss();
    let toastId = toast.loading("loading...");
    setLoading(true);
    try {
      let data = await deleteUrl(selectedUrl, state?.activeTab);
      toast.dismiss(toastId);
      if (data.status === "URL deleted successfully.") {
        toast.success(`URL(${selectedUrl}) deleted successfully.`);
      } else {
        toast.error(`URL (${selectedUrl}) ${data.status ? data.status : ""}`);
      }
    } catch (err) {
      console.log("Error: ", err);
    }
    setLoading(false);
    setDelelteUrlModalOpen(false);
    fetchDocuments(state?.activeTab);
    setPage(0);
  };

  // handle cancel delete or edit in modal
  const handleNo = async () => {
    setSelectedUrl(null);
    setEditUrlModalOpen(false);
    setDelelteUrlModalOpen(false);
  };

  // Export to Excel
  const handleExportExcel = () => {
    const worksheetData = urlDataLocal.map((pdf) => ({
      "URL Name": pdf.name,
      "URL Path": pdf.url
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "PDF List");

    XLSX.writeFile(workbook, "url_list.xlsx");
  };

  // Export to PDF - Fixed version
  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();

      // Add title
      doc.setFontSize(16);
      doc.text("List of PDFs", 14, 20);

      // Prepare table data
      const tableColumn = ["URL Name", "URL Path"];
      const tableRows = urlDataLocal.map((url) => [
        url.name || "",
        url.url || ""
      ]);

      // Add table using autoTable
      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 30,
        styles: {
          fontSize: 10,
          cellPadding: 3,
          overflow: "linebreak",
          cellWidth: "wrap"
        },
        columnStyles: {
          0: { cellWidth: 60 }, // URL Name column
          1: { cellWidth: 120 } // URL Path column
        },
        headStyles: {
          fillColor: [71, 85, 105], // Gray color for header
          textColor: 255,
          fontStyle: "bold"
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245] // Light gray for alternate rows
        },
        margin: { left: 14, right: 14 },
        tableWidth: "auto"
      });

      // Save the PDF
      doc.save("url_list.pdf");
      toast.success("PDF exported successfully!");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Failed to export PDF. Please try again.");
    }
  };

  //set url data in local state
  useEffect(() => {
    setUrlDataLocal(urlData);
  }, [urlData]);

  //get url data on page load
  useEffect(() => {
    getData();
  }, []);

  return (
    <div className="h-[calc(100%_-_56px)] pb-2 mt-2">
      <div className="flex items-center justify-between">
        <p className="text-lg font-medium text-[#424242]">List of URLs</p>
        <div className="flex items-center gap-2">
          <button
            className="flex items-center justify-center gap-1.5 h-8 rounded-md bg-[#C7222A] text-white text-sm w-[7.5rem]"
            onClick={handleUpload}
          >
            <p>Upload URL</p>
            <img className="h-[14px]" src={assetsManager?.uploadIcon} alt="" />
          </button>
          {/* Export Dropdown */}
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation(); // prevent outside click listener from closing it instantly
                setExportDropdownOpen(!exportDropdownOpen);
              }}
              className="flex items-center justify-center gap-1.5 h-8 rounded-md bg-gray-700 text-white text-sm w-[7.5rem]"
            >
              Export ▼
            </button>

            {exportDropdownOpen && (
              <div className="absolute right-0 mt-1 w-32 bg-white border rounded shadow-lg z-50">
                <button
                  onClick={() => {
                    handleExportExcel();
                    setExportDropdownOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                >
                  Excel
                </button>
                <button
                  onClick={() => {
                    handleExportPDF();
                    setExportDropdownOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                >
                  PDF
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2 h-[calc(100%_-_32px)]">
        <div className="w-full relative">
          <img
            className="absolute top-1/2 transform translate-y-[-50%] left-[14px] h-[13px]"
            src={assetsManager.searchIcon}
            alt=""
          />
          <input
            className="w-[24rem] rounded-[4px] p-2 px-9 focus:outline-none bg-[#EFEFEF] text-sm"
            placeholder="Search URL"
            onChange={handleChangeSearchInput}
            value={searchInput}
          />
        </div>
        <div className="h-[calc(100%_-_36px)] overflow-auto relative flex flex-col justify-between">
          <table
            className={`w-full table-fixed ${
              urlDataLocal?.length === 0 && "sticky top-0"
            }`}
          >
            <thead className="sticky top-0">
              <tr>
                <th className="border-b-[2px] border-[#EAECF0] text-sm p-3 bg-[#F8F8F8] font-medium text-start">
                  <p>URL Name</p>
                </th>
                <th className="border-b-[2px] border-[#EAECF0] text-sm p-3 bg-[#F8F8F8] font-medium text-start">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {urlDataLocal?.length > 0 &&
                urlDataLocal
                  ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item, i) => {
                    return (
                      <tr className="border-b-[2px] border-[#EAECF0]" key={i}>
                        <td
                          className=" text-sm p-3 text-start text-[#1C4EB2]"
                          title={item?.url}
                        >
                          <p
                            onClick={() => handleOpenUrl(item?.url)}
                            className="text-ellipsis text-nowrap overflow-hidden max-w-[90%] w-fit cursor-pointer"
                          >
                            {item?.url}
                          </p>
                        </td>
                        <td className=" text-sm p-3 text-start">
                          <div className="flex gap-1 h-full">
                            <div
                              onClick={() => handleEditUrl(item)}
                              className="flex justify-center p-2 cursor-pointer h-full"
                            >
                              <img
                                className="h-[13px] w-[17px]"
                                src={assetsManager.edit}
                                alt=""
                              />
                            </div>
                            <div
                              onClick={() => handleOpenUrl(item.url)}
                              className="flex justify-center p-2 cursor-pointer h-full"
                            >
                              <img
                                className="h-[13px] w-[14px]"
                                src={assetsManager.eye}
                                alt=""
                              />
                            </div>
                            <div
                              onClick={() => handleDeleteUrl(item)}
                              className="flex items-center justify-center p-2 cursor-pointer h-full"
                            >
                              <img
                                className="h-[13px] w-[17px]"
                                src={assetsManager.deleteIcon}
                                alt=""
                              />
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
          {urlDataLocal?.length === 0 && (
            <div className=" h-[calc(100%_-_36px)] flex items-center justify-center">
              {loading ? (
                "Loading..."
              ) : (
                <img
                  className="h-2/3 min-h-24 max-h-48"
                  src={assetsManager.emptyIllustration}
                  alt=""
                />
              )}
            </div>
          )}
        </div>
        <Pagination
          data={urlDataLocal}
          rowsPerPage={rowsPerPage}
          page={page}
          handleChangePage={handleChangePage}
        />
      </div>
      {uploadUrlModalOpen && (
        <UploadUrlModal setUploadUrlModalOpen={setUploadUrlModalOpen} />
      )}
      {delelteUrlModalOpen && (
        <AlertModal
          alertMessage={deleteAlertMessage}
          handleYes={handleDelete}
          handleNo={handleNo}
        />
      )}
      {editUrlModalOpen && (
        <AlertModal
          alertMessage={rescrapeAlertMessage}
          handleYes={handleEdit}
          handleNo={handleNo}
        />
      )}
    </div>
  );
};

export default Url;
